import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { TokenService } from './token.service';
import {
  getErrorFromRegionResponse,
  handleCheckIsSuccess,
} from './handle-dr-response';
import { storeRegionTokens } from './store-region-tokens.util';

@Injectable({ providedIn: 'root' })
export class DrAuthService {
  private refreshTimeout: any;
  private url = environment.graphqlUrl;
  private baseKey = `CognitoIdentityServiceProvider.${environment.clientId}`;

  constructor(
    private http: HttpClient,
    private ngZone: NgZone,
    private tokenService: TokenService
  ) {}

  private getEmail(): string | null {
    return localStorage.getItem(`${this.baseKey}.LastAuthUser`);
  }

  private getPrimaryKey(email: string): string {
    return `${this.baseKey}.${email}`;
  }

  private getSecondaryKey(email: string): string {
    return `CognitoIdentityServiceProvider.${environment.clientIdSecondary}.${email}.2`;
  }

  private getLegacySecondaryKey(email: string): string {
    return `CognitoIdentityServiceProvider.${environment.clientIdSecondary}.${email}.secondary`;
  }

  private getToken(key: string, suffix: string): string | null {
    return localStorage.getItem(`${key}.${suffix}`);
  }

  get expireAt(): number {
    const email = this.getEmail();
    if (!email) return 0;

    const key = this.getPrimaryKey(email);
    const expiresAt = this.getToken(key, 'expiresat');
    return expiresAt ? +expiresAt - 60 : 0;
  }

  shouldRefresh(): boolean {
    const exp = this.expireAt;
    return exp !== 0 && exp < Date.now() / 1000;
  }

  refreshToken(): Observable<void> {
    const email = this.getEmail();
    if (!email) return of(undefined);

    const primaryKey = this.getPrimaryKey(email);
    const secondaryKey = this.getSecondaryKey(email);
    const legacySecondaryKey = this.getLegacySecondaryKey(email);

    const primaryToken = this.getToken(primaryKey, 'refreshToken');
    const secondaryToken =
      this.getToken(secondaryKey, 'refreshToken') ??
      this.getToken(legacySecondaryKey, 'refreshToken');

    if (!primaryToken && !secondaryToken) return of(undefined);

    const headers = {
      Authorization: 'NO_AUTHENTICATION',
      Authorization2: 'NO_AUTHENTICATION',
    };

    return this.http
      .post<any>(
        `${this.url}`,
        {
          query: `
          mutation RefreshToken($refreshTokens: [String!]!) {
            refreshToken(refreshTokens: $refreshTokens) {
              regions {
                name
                success
                idtoken
                accesstoken
                refreshtoken
                expiresat
                error
              }
              error {
                code
                message
              }
            }
          }
    `,
          variables: {
            refreshTokens: [primaryToken || '', secondaryToken || ''],
          },
        },
        { headers }
      )
      .pipe(
        map((res) => {
          const data = res.data.refreshToken;
          if (res.data.error) {
            throw new Error(res.error.message);
          } else if (handleCheckIsSuccess(data.regions)) {
            storeRegionTokens(this.tokenService, data.regions, email);
            this.init();
          } else {
            const errors = getErrorFromRegionResponse(data.regions);
            throw new Error(errors.join(', '));
          }
        })
      );
  }

  scheduleRefresh(): void {
    const delay = (this.expireAt - Date.now() / 1000) * 1000;
    if (delay <= 0) return;

    this.clearTimer();

    console.log(`Scheduling token refresh in ${delay}ms`);

    this.ngZone.runOutsideAngular(() => {
      this.refreshTimeout = setTimeout(() => {
        this.ngZone.run(() =>
          this.refreshToken().subscribe({ error: console.error })
        );
      }, delay);
    });
  }

  clearTimer(): void {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }
  }

  async init(): Promise<void> {
    console.log('DrAuthService initialized');
    if (this.shouldRefresh()) {
      await this.refreshToken().toPromise().catch(console.error);
    }
    this.scheduleRefresh();
    this.handshake();
  }

  handshake(): void {
    const email = this.getEmail();
    if (!email) return;

    const primaryKey = this.getPrimaryKey(email);
    const secondaryKey = this.getSecondaryKey(email);
    const legacySecondaryKey = this.getLegacySecondaryKey(email);

    const primaryToken = this.getToken(primaryKey, 'idToken');
    const secondaryToken =
      this.getToken(secondaryKey, 'idToken') ??
      this.getToken(legacySecondaryKey, 'idToken');

    if (!!primaryToken !== !!secondaryToken) {
      const { drRegion, drRegionSecondary } = environment;

      this.http
        .post<any>(`${this.url}`, {
          query: `
          query Handshake {
                handshake {
                  regions {
                    name
                    success
                    idtoken
                    accesstoken
                    refreshtoken
                    expiresat
                    error
                    __typename
                  }
                  error {
                    code
                    message
                    __typename
                  }
                  challenge {
                    challenge
                    challengename
                    session
                    username
                    phonenumbersuffix
                    medium
                    isphoneverified
                    challengetype
                    __typename
                  }
                  __typename
                }
              }      
          `,
        })
        .pipe(
          map((res) => {
            if (res?.data && res?.data?.handshake) {
              const tokens = res.data.handshake;
              // Use primary token if available, otherwise fallback to secondary
              let primaryTokenData = tokens.regions.find(
                (item: any) => item.name === drRegion
              );
              let secondaryTokenData = tokens.regions.find(
                (item: any) => item.name === drRegionSecondary
              );

              if (
                (!primaryTokenData || !primaryTokenData.success) &&
                secondaryTokenData &&
                secondaryTokenData.success
              ) {
                console.warn(
                  `Primary region (${drRegion}) token not available during handshake, using secondary`
                );
                primaryTokenData = secondaryTokenData;
                secondaryTokenData = null;
              }

              this.tokenService.setTokens(
                primaryTokenData,
                secondaryTokenData,
                email
              );
            } else if (res?.errors?.length > 0) {
              throw new Error(res.errors.map((e: any) => e.message).join(', '));
            }
          })
        )
        .subscribe({
          error: console.error,
        });
    }
  }
}
